import { NestedBubbleMetadata } from '@/components/public/Visualisations/NestedBubbles'
import { Shapes } from '@/components/public/Visualisations/types'
import Konva from 'konva'
import _isEqual from 'lodash/isEqual'

const WORD_BREAK_PATTERN = /\s+|-/
const MIN_FONT_SIZE = 2

export class NestedBubbleText extends Konva.Text implements Shapes.Shape {
  static readonly shapeName = 'NestedBubbleText'
  private _metadata: NestedBubbleMetadata
  private _ellipsis: boolean = true
  className = NestedBubbleText.shapeName
  capabilities: Shapes.Capability[] = []

  constructor(metadata: NestedBubbleMetadata) {
    super({
      text: metadata.label,
      fill: metadata.fontColour,
      align: 'center',
      verticalAlign: 'middle',
      listening: false,
    })

    this._metadata = metadata
    this.width(this._sideLength)
    this.height(this._sideLength)
    this.x(this._x)
    this.y(this._y)
    this.fontSize(this._fontSize)

    // if the text doesn't fit then don't wrap it at all
    // otherwise Konva can wrap half-way through a word
    if (this._ellipsis) {
      this.wrap('none')
      this.ellipsis(true)
    }
  }

  get key() {
    return this.path.toString()
  }

  get path() {
    return this._metadata.path
  }

  get center() {
    return this.position()
  }

  refresh() {
    this.fill(this._metadata.fontColour)
  }

  private get _sideLength() {
    return this._metadata.radius * Math.sqrt(2)
  }

  private get _x() {
    return this._metadata.position.x - this._sideLength / 2
  }

  private get _y() {
    return this._metadata.position.y - this._sideLength / 2
  }

  private get _fontSize() {
    const sideLength = this._sideLength
    let fontSize = sideLength / 3
    const words = this._metadata.label
      .split(WORD_BREAK_PATTERN)
      .filter((word) => word.trim() !== '')
    const labelShape = new Konva.Text({
      text: this._metadata.label,
      width: sideLength,
      height: sideLength,
      fontSize,
    })

    while (fontSize >= MIN_FONT_SIZE) {
      if (this.containsAllWholeWords(labelShape, words)) {
        this._ellipsis = false
        break
      }

      fontSize -= fontSize > 10 ? 2 : 1 // Larger decrement for larger sizes
      labelShape.fontSize(fontSize)
    }

    return Math.max(fontSize, MIN_FONT_SIZE)
  }

  // Konva.Text objects contain an array of the rendered strings after word wrapping
  // If all of the text cannot be rendered then parts of it are missing from this array.
  // We can utilize this to ensure that all of the words of the label are rendered and
  // they are rendered as whole words (not wrapped half-way through a word)
  private containsAllWholeWords(textShape: Konva.Text, requiredWords: string[]) {
    const renderedWords = textShape.textArr
      .flatMap((el) => el.text.split(WORD_BREAK_PATTERN))
      .filter((word) => word.trim() !== '')
    return _isEqual(renderedWords, requiredWords)
  }
}
